import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useEffect, useState, Fragment, } from 'react';
import classnames from 'classnames/bind';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { formFieldWrapper } from '../formFieldWrapper';
import Button from '../../elementals/button';
import { ColorNames } from '../../../constants/colorVars';
import styles from './styles.scss';
const cx = classnames.bind(styles);
const BREAKPOINT_TO_TRUNCATE = 500;
const InlineSelectField = ({ elmId, options: _options, value, mode = 'single', onChange, onBlur, onFocus, showError = false, fluid = false, hideWrapper = false, focused = false, disabled = false, compact = false, className = '', showAnyOption = true, anyOptionText = 'Any', }) => {
    const { width } = useWindowDimensions();
    const [options, setOptions] = useState(_options);
    const normalizedValue = useMemo(() => {
        if (value === undefined || value === null || value === '')
            return [];
        return Array.isArray(value)
            ? value.filter(v => v !== '' && v !== null && v !== undefined)
            : [value];
    }, [value]);
    const isAnySelected = normalizedValue.length === 0;
    const allOptionValues = useMemo(() => options.map(option => option.value), [options]);
    const getSelectionSummary = useMemo(() => {
        if (isAnySelected)
            return 'No filters applied';
        if (mode === 'single')
            return `${normalizedValue[0]} selected`;
        if (mode === 'multi')
            return `${normalizedValue.length} options selected: ${normalizedValue.join(', ')}`;
        if (mode === 'range')
            return `Range selected: ${normalizedValue.join(' to ')}`;
        return '';
    }, [isAnySelected, mode, normalizedValue]);
    const getModeDescription = useMemo(() => {
        switch (mode) {
            case 'single':
                return 'Single selection mode. Choose one option.';
            case 'multi':
                return 'Multiple selection mode. Click options to toggle selection.';
            case 'range':
                return 'Range selection mode. Click two options to select a range.';
            default:
                return '';
        }
    }, [mode]);
    useEffect(() => {
        if (width < BREAKPOINT_TO_TRUNCATE) {
            const truncatedOptions = _options.map(option => ({
                ...option,
                text: option.text.length > 3 ? `${option.text.slice(0, 3)}.` : option.text,
            }));
            setOptions(truncatedOptions);
        }
        else {
            setOptions(_options);
        }
    }, [width, _options]);
    const handleOptionClick = useCallback((optionValue) => {
        if (disabled || !onChange)
            return;
        let newValue;
        if (mode === 'single') {
            newValue = optionValue;
        }
        else if (mode === 'multi') {
            const currentValues = [...normalizedValue];
            const valueIndex = currentValues.indexOf(optionValue);
            if (valueIndex > -1) {
                currentValues.splice(valueIndex, 1);
            }
            else {
                currentValues.push(optionValue);
            }
            newValue = currentValues;
        }
        else if (mode === 'range') {
            const optionIndex = allOptionValues.indexOf(optionValue);
            if (normalizedValue.length === 0) {
                newValue = [optionValue];
            }
            else if (normalizedValue.length === 1) {
                const firstIndex = allOptionValues.indexOf(normalizedValue[0]);
                const startIndex = Math.min(firstIndex, optionIndex);
                const endIndex = Math.max(firstIndex, optionIndex);
                newValue = allOptionValues.slice(startIndex, endIndex + 1);
            }
            else {
                newValue = [optionValue];
            }
        }
        else {
            newValue = optionValue;
        }
        onChange(newValue);
    }, [disabled, onChange, mode, normalizedValue, allOptionValues]);
    const handleAnyClick = useCallback(() => {
        if (disabled || !onChange)
            return;
        onChange(mode === 'single' ? '' : []);
    }, [disabled, onChange, mode]);
    const isOptionSelected = useCallback((optionValue) => {
        return normalizedValue.includes(optionValue);
    }, [normalizedValue]);
    const handleFocus = useCallback(() => {
        if (onFocus)
            onFocus();
    }, [onFocus]);
    const handleBlur = useCallback(() => {
        if (onBlur)
            onBlur();
    }, [onBlur]);
    const wrapperClasses = cx('inline-select-field', 'field', {
        fluid,
        error: showError,
        focused,
        disabled,
        compact,
    }, className);
    const renderOptions = () => (_jsxs(_Fragment, { children: [showAnyOption && (_jsx(Button, { role: isAnySelected ? 'secondary' : 'tertiary', size: compact ? 'small' : 'medium', color: isAnySelected ? ColorNames.Action : ColorNames.Gray, isSelected: isAnySelected, onClick: handleAnyClick, onFocus: handleFocus, onBlur: handleBlur, disabled: disabled, elmId: `${elmId}_any_option`, className: cx('option-button', { compact, selected: isAnySelected }), "aria-pressed": isAnySelected, "aria-describedby": `${elmId}_description`, "aria-label": `${anyOptionText}, clear all selections`, children: anyOptionText }, 'any')), _jsx("div", { className: cx('gap-filler') }, 'gap-filler-any'), options.map((option, index) => {
                const isSelected = isOptionSelected(option.value);
                const isOptionDisabled = disabled || option.disabled;
                const isPrevSelected = index > 0 && isOptionSelected(options[index - 1].value);
                const isNextSelected = index < options.length - 1 &&
                    isOptionSelected(options[index + 1].value);
                const getAriaLabel = () => {
                    let label = option.text;
                    if (isSelected) {
                        label += ', selected';
                    }
                    if (isOptionDisabled) {
                        label += ', disabled';
                    }
                    if (mode === 'multi') {
                        label += isSelected ? ', click to deselect' : ', click to select';
                    }
                    else if (mode === 'range') {
                        if (normalizedValue.length === 0) {
                            label += ', click to start range selection';
                        }
                        else if (normalizedValue.length === 1) {
                            label += ', click to complete range selection';
                        }
                        else {
                            label += ', click to start new range';
                        }
                    }
                    return label;
                };
                return (_jsxs(Fragment, { children: [_jsx(Button, { role: isSelected ? 'secondary' : 'tertiary', size: compact ? 'small' : 'medium', color: isSelected ? ColorNames.Action : ColorNames.Gray, isSelected: isSelected, onClick: () => handleOptionClick(option.value), onFocus: handleFocus, onBlur: handleBlur, disabled: isOptionDisabled, elmId: `${elmId}_option_${option.value}`, className: cx('option-button', {
                                selected: isSelected,
                                'prev-selected': isPrevSelected,
                                'next-selected': isNextSelected,
                                compact,
                            }, 'super-compact'), "aria-pressed": isSelected, "aria-describedby": `${elmId}_description`, "aria-label": getAriaLabel(), "aria-setsize": options.length + (showAnyOption ? 1 : 0), "aria-posinset": index + (showAnyOption ? 2 : 1), children: option.text }, option.value), index < options.length - 1 && (_jsx("div", { className: cx('gap-filler', {
                                selected: isSelected && isNextSelected,
                            }) }, `gap-${index}`))] }, option.value));
            })] }));
    if (hideWrapper) {
        return (_jsxs(_Fragment, { children: [_jsxs("div", { id: `${elmId}_description`, className: 'sr-only', children: [getModeDescription, " ", getSelectionSummary] }), renderOptions()] }));
    }
    return (_jsxs("div", { className: wrapperClasses, "data-elm-id": elmId, role: 'group', "aria-label": `${mode} selection field`, "aria-describedby": `${elmId}_description ${elmId}_summary`, "aria-required": false, "aria-invalid": showError, "aria-disabled": disabled, children: [_jsx("div", { id: `${elmId}_description`, className: 'sr-only', children: getModeDescription }), _jsx("div", { id: `${elmId}_summary`, className: 'sr-only', "aria-live": 'polite', "aria-atomic": 'true', children: getSelectionSummary }), renderOptions()] }));
};
const InlineSelectFieldWrapped = formFieldWrapper((fieldProps, showError) => {
    return _jsx(InlineSelectField, { ...fieldProps, showError: showError });
});
export default InlineSelectFieldWrapped;
//# sourceMappingURL=index.js.map